// =============================================================================
// Admin view of a user's bank accounts — GET /admin/users/{id}/bank-accounts
// =============================================================================
// Since defect DR-24 these rows carry a manual-verification audit trail.
// Payouts are gated on `verification_status === "verified"`, and the ONLY
// way to reach that state is an admin attesting they eyeballed a document
// (cancelled cheque / statement / passbook / manual penny drop). There is
// no automated verifier — `verified` is a server-derived convenience flag
// that is true iff the status is "verified".
// =============================================================================

/// Lifecycle of the manual verification. `unverified` is the resting state
/// for an account nobody has looked at; `pending` means the user (or the
/// DR-24 migration) queued it for review.
export type BankVerificationStatus =
  | "unverified"
  | "pending"
  | "verified"
  | "rejected";

/// What document the admin checked the account details against. Sent with
/// every decision so the audit trail records *why* the admin was confident.
export type BankVerificationBasis =
  | "cancelled_cheque"
  | "bank_statement"
  | "passbook"
  | "penny_drop_manual"
  | "other";

export interface AdminUserBankAccountRow {
  id: string;
  /// Owner of the account. Redundant on /users/:userId (the route already
  /// knows the owner) but load-bearing on the pending queue, which spans
  /// users and builds each row's decision URL from it.
  user_id: string;
  account_holder_name: string;
  account_number_last_4: string;
  ifsc_code: string;
  bank_name: string;
  branch_name: string | null;
  is_primary: boolean;
  verification_status: BankVerificationStatus;
  /// Derived server-side; true only when verification_status === "verified".
  verified: boolean;
  verification_requested_at: string | null;
  verified_at: string | null;
  verified_by_admin_id: string | null;
  verification_basis: string | null;
  verification_notes: string | null;
  upi_handle: string | null;
  created_at: string;
}

export interface AdminUserBankAccountsResponse {
  rows: AdminUserBankAccountRow[];
}

/// Body of POST /admin/users/{user_id}/bank-accounts/{account_id}/verification.
/// `notes` is REQUIRED by the backend when basis === "other" or the decision
/// is "rejected" — the UI mirrors that rule client-side so the admin gets
/// the feedback before the round trip.
export interface BankVerificationDecisionPayload {
  decision: "verified" | "rejected";
  basis: BankVerificationBasis;
  notes?: string;
}

/// Human-readable labels for the basis select. Kept next to the union so a
/// new basis can't be added to the type without someone noticing the label
/// map needs a matching entry (Record<> makes it a type error).
export const BANK_VERIFICATION_BASIS_LABELS: Record<
  BankVerificationBasis,
  string
> = {
  cancelled_cheque: "Cancelled cheque",
  bank_statement: "Bank statement",
  passbook: "Passbook",
  penny_drop_manual: "Penny drop (manual)",
  other: "Other",
};
